import _                                      from 'underscore';
import { v4 as uuid }                         from 'uuid';
import React, { useEffect, useRef, useState } from 'react';
import { Form }                               from 'semantic-ui-react';
import { Formik, FormikConfig, FormikProps }  from 'formik';
import * as Yup                               from 'yup';
import { useQuery }                           from '@apollo/react-hooks';

import { findTopSimilarityString, runIfFunction }                                            from '@src/util';
import { Align, CorvalCategory, CorvalStaticProjectActivity, Size, Uuid }                    from '@src/types';
import { queriesLoading }                                                                    from '@src/store/_apolloUtil';
import { BasicDialogContainer }                                                              from '../../../components/modals/BasicDialogContainer';
import { StyledContainer }                                                                   from '../../../containers';
import { LoadingIndicator }                                                                  from '../../../components/util/loading/LoadingIndicator';
import { DecisionEngineQueries }                                                             from "@src/queries";
import { ActivityQueries }                                                                   from "@src/queries/ActivityQueries";
import { BasicDialogProps }                                                                  from "@src/views/components/modals/BasicDialog";
import { HierarchyTaxonomyType }                                                             from "@src/views/pages/projects/mapping/gantt/tools/TaskFunctions";
import { addStringMinCharDescriptorTest, useCategories, useDecisionEngine, useFeatureFlags } from "@src/hooks";
import { TextAreaField, TextField }                                                          from "@src/views/components/forms";
import { WarningMessage }                                                                    from "@src/views/components/util/messaging/WarningMessage";
import { SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD }                                        from "@src/constants";
import { ProjectCategoryDropdown }                                                           from "@src/views/components/forms/dropdowns/ProjectCategoryDropdown";
import { QueryOpDialog }                                                                     from "@src/views/components/from-query/QueryOpDialog";
import { ProjectQueries }                                                                    from "@src/queries/ProjectQueries";


export interface AddNewProjectTaxonomyDialogOnCompleteReturnData {
    taxonomy: HierarchyTaxonomyType[],
}

interface FormValues {
    id: Uuid
    name: string,
    description: string,
    categoryId: Uuid | null,
    ordinal?: number,
}

type FormConfig = FormikConfig<FormValues>;

export const getInitialValues = ( values: Partial<FormValues> = {} ): FormValues => ( {
    id:          uuid(),
    name:        '',
    description: '',
    categoryId:  null,
    ordinal:     0,
    ...values
} );

interface AddNewProjectTaxonomyDialogProps extends BasicDialogProps {
    projectId: Uuid,
    projectVersionId: string,
    categoryId?: boolean,
    atIndex?: number,
    maxNamingScoreThreshold?: number,

    onComplete?( data: AddNewProjectTaxonomyDialogOnCompleteReturnData ): void,
}

const AddNewProjectTaxonomyDialogDefaultProps = {
    projectId:               null,
    projectVersionId:        null,
    categoryId:              true,
    atIndex:                 null,
    maxNamingScoreThreshold: SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD,
} satisfies Partial<AddNewProjectTaxonomyDialogProps>

function AddNewProjectTaxonomyDialog( __props: AddNewProjectTaxonomyDialogProps ){

    const _props = { ...AddNewProjectTaxonomyDialogDefaultProps, ...__props };

    const { projectId, projectVersionId, categoryId, ...props } = _props,
          [ existingTaxonomyNames, setExistingTaxonomyNames ]   = useState( [] ),
          [ maxScoreThreshold ]                                 = useState( props.maxNamingScoreThreshold ),
          { getNextCategoryOrdinal, getNextSubCategoryOrdinal } = useDecisionEngine(),
          { findAllCategoryNames, findAllSubCategoryNames }     = useCategories(),
          featureFlags                                          = useFeatureFlags(),
          formikRef                                             = useRef<FormikProps<FormValues>>( null );

    const isSubCategory = !_.isEmpty( categoryId );

    // Get all DEM subcategories that are available
    const getAvailableCategoryReq = useQuery<{ categories: CorvalCategory[] }>( DecisionEngineQueries.queries.GET_CATEGORIES( `id name displayName ordinal description subCategories{ id name displayName ordinal description }` ), {
        fetchPolicy: 'network-only'
    } );

    // Get all static project activities for the given project version and the taxonomy info therein
    const getStaticProjectActivitiesReq = useQuery<{ staticProjectActivities: CorvalStaticProjectActivity[] }>( ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `id categoryId categoryName subCategoryId subCategoryName`, projectVersionId ), {
        variables:   { projectVersionId },
        skip:        ( !projectId || !projectVersionId ),
        fetchPolicy: 'network-only'
    } );

    const isLoading = queriesLoading( [ getAvailableCategoryReq, getStaticProjectActivitiesReq ] );

    const getAllTaxonomyNamesForComparison = async () => {

        return isSubCategory
               ? await findAllSubCategoryNames( projectId, projectVersionId )
               : await findAllCategoryNames( projectId, projectVersionId );
    }

    useEffect( () => {

        getAllTaxonomyNamesForComparison().then( existingNames => {

            setExistingTaxonomyNames( existingNames );
        } )

    }, [] );

    /* FORM SETUP
     ====================================== */

    addStringMinCharDescriptorTest( 'hasDescriptorMinChar', {
        minChar: featureFlags.systemSettingFormFieldsDescriptorsMinCharacterLength
    } );

    let formProps: FormConfig = {

        innerRef:           formikRef,
        enableReinitialize: true,
        isInitialValid:     false,
        initialValues:      getInitialValues( { categoryId } ),
        validateOnChange:   true,

        validationSchema: Yup.object( {
            name:        Yup.string().min( 3 ).required( 'Name is required' ),
            description: Yup.string().hasDescriptorMinChar(),
            categoryId:  Yup.mixed().when( [], {
                is:        ( value ) => _.isEmpty( value ) && isSubCategory,
                then:      Yup.mixed().defined().required( 'You must choose a parent category.' ),
                otherwise: Yup.mixed(),
            } ),
        } ),

        onSubmit: null,
    };

    const getGraphArgs = () => {

        const { values } = formikRef.current;

        const changeKey = isSubCategory
                          ? 'subCategoryChanges'
                          : 'categoryChanges';

        const data = {
            id:          values.id,
            name:        values.name,
            description: values.description,
            displayName: values.name,
            ordinal:     values.ordinal,
        };

        if( isSubCategory ){
            data[ 'categoryId' ] = values.categoryId;
        }

        return {
            variables: {
                input: {
                    projectVersionId: projectVersionId,
                    [ changeKey ]:    {
                        added: [ data ]
                    }
                }
            }
        };
    }

    const handleOnSubmit = async () => {

        const { values } = formikRef.current;

        const ordinal = isSubCategory
                        ? await getNextSubCategoryOrdinal( values.categoryId )
                        : await getNextCategoryOrdinal();

        formikRef.current.setFieldValue( 'ordinal', ordinal );

        return Promise.resolve( formikRef.current.values );
    }

    const handleOnComplete = async ( data: any ) => {

        const { values } = formikRef.current;

        const availableCategoryReq = await getAvailableCategoryReq.refetch(),
              taxonomy             = [];

        if( availableCategoryReq.data ){

            const { data: { categories } } = availableCategoryReq;

            let newTaxonomy = null;

            if( isSubCategory ){

                newTaxonomy = {
                    category:    _.findWhere( categories, { id: values.categoryId } ),
                    subCategory: _.chain( categories ).pluck( 'subCategories' ).flatten().findWhere( { id: values.id } ).value()
                };

            } else {

                newTaxonomy = {
                    category:    _.findWhere( categories, { id: values.id } ),
                    subCategory: null
                };
            }

            if( newTaxonomy ){
                taxonomy.push( newTaxonomy )
            }
        }

        runIfFunction( props.onComplete, { taxonomy } );
    }

    return (

        <Formik<FormValues> { ...formProps }>

            { formik => {

                const existingNameResultMatch = findTopSimilarityString( formik.values.name, existingTaxonomyNames, maxScoreThreshold, {
                          useJaroWinkler: false,
                      } ),
                      hasNameWarning          = !_.isNull( existingNameResultMatch ),
                      taxonomyTypeLabel       = isSubCategory ? 'Subcategory' : 'Category';

                return (

                    isLoading
                    ? (
                        <StyledContainer padding={ Size.LG }>
                            <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Collecting Category Information...' }/>
                        </StyledContainer>
                    ) : (
                        <QueryOpDialog
                            { ...props }

                            mutation={ ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA() }
                            graphArgs={ getGraphArgs }
                            onSubmit={ handleOnSubmit }
                            onComplete={ handleOnComplete }
                            onError={ props.onError }
                            onClose={ props.onClose }
                            // confirmationHandler={ handleConfirmOperation }

                            operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                            operationIsHidden={ !projectId || !projectVersionId }
                            operationInTransitMessage={ `Creating ${ taxonomyTypeLabel }...` }
                            submitLabel={ `Create ${ taxonomyTypeLabel }` }
                            className={ 'no-padding' }
                            alignControls={ Align.CENTER }>

                            <Form>

                                <BasicDialogContainer
                                    title={ `New Project ${ taxonomyTypeLabel }` }
                                    contentMaxHeight={ '60dvh' }
                                    contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                    overflow={ true }
                                    scrolling={ true }
                                    roundedTop>

                                    { hasNameWarning && (
                                        <WarningMessage>
                                            <p className={ 'text-center' }>Corval recommends providing a more discrete name.<br/>The current name closely matches the existing { taxonomyTypeLabel.toLowerCase() }:<br/><b>"{ existingNameResultMatch.match }"</b></p>
                                        </WarningMessage>
                                    ) }

                                    { isSubCategory && (
                                        <ProjectCategoryDropdown
                                            projectId={ projectId }
                                            projectVersionId={ projectVersionId }
                                            label={ 'Parent Category' }
                                            name={ 'categoryId' }
                                            loading={ isLoading }
                                        />
                                    ) }

                                    <TextField
                                        type='text'
                                        label={ 'Name' }
                                        name={ 'name' }
                                        loading={ isLoading }/>

                                    <TextAreaField
                                        label={ 'Description' }
                                        name={ 'description' }
                                        rows={ 5 }
                                        placeholder=''
                                        loading={ isLoading }/>

                                </BasicDialogContainer>
                            </Form>

                        </QueryOpDialog>
                    )
                )
            } }

        </Formik>
    )
}

export {
    AddNewProjectTaxonomyDialog,
    AddNewProjectTaxonomyDialogDefaultProps,
    type AddNewProjectTaxonomyDialogProps,
};
